import { Injectable } from '@angular/core';
import { Provider } from '@app/models';
import { SustainabilityService } from '@app/services/api/sustainability.service';
import { FlashService } from '@app/services/flash.service';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import {
  catchError,
  map,
  switchMap,
  concatMap,
  tap,
  exhaustMap
} from 'rxjs/operators';
import {
    SustainabilityActionTypes,
    LoadFollowParents,
    LoadFollowParentsSuccess,
    LoadFollowParentsFail,
    LoadProvidersByParentId,
    LoadProvidersByParentIdSuccess,
    LoadProvidersByParentIdFail,
    ExportByProviderId,
    ExportByProviderIdSuccess,
    ExportByProviderIdFail,
    AcceptedChanges,
    AcceptedChangesSuccess,
    AcceptedChangesFail,
    UploadProcessFileSuccess,
    ExportGroup,
    ExportGroupSuccess,
    ExportGroupDownload,
    ExportGroupDownloadSuccess,
    ExportGroupDownloadFail,
    ClearState,
    GetLink,
    GetLinkFail,
    UploadGroup,
    UploadGroupSuccess,
    UploadGroupFail,
    UploadingGroupPending,
    UploadingGroupSuccess,
    ExportConsolidate
} from '@core/actions/sustainability.action';


@Injectable()
export class SustainabilityEffects {
  @Effect()
  loadFollowParents$: Observable<Action> = this.actions$.pipe(
    ofType<LoadFollowParents>(SustainabilityActionTypes.LoadFollowParents),
    switchMap(action => {
      return this.sustainabilityApi.getFollowParentsIds().pipe(
        map((parentsIds: any) => {
            return new LoadFollowParentsSuccess(parentsIds)
        }),
        catchError(error => of(new LoadFollowParentsFail(error)))
      );
    })
  );

  @Effect()
  loadProvidersByParentId$: Observable<Action> = this.actions$.pipe(
    ofType<LoadProvidersByParentId>(SustainabilityActionTypes.LoadProvidersByParentId),
    switchMap(action => {
      return this.sustainabilityApi.getProvidersByParentId(action.parentId).pipe(
        map((parentsIds: any) => {
            return new LoadProvidersByParentIdSuccess(parentsIds)
        }),
        catchError(error => of(new LoadProvidersByParentIdFail(error)))
      );
    })
  );

  @Effect({ dispatch: false })
  exportByProviderId$ = this.actions$.pipe(
    ofType<ExportByProviderId>(SustainabilityActionTypes.ExportByProviderId),
    map(action => [action.parentId, action.providerId, action.providerName]),
    tap(([parentId, providerId, providerName]) => {
      this.sustainabilityApi.exportProviders(parentId, providerId, providerName);
    })
  );

  @Effect()
	getLink$: Observable<Action> = this.actions$.pipe(
		ofType<GetLink>(SustainabilityActionTypes.GetLink),
		map(action => [action.file, action.parentId]),
		switchMap(([file, parentId]) => {
			return this.sustainabilityApi.getLink(file, parentId).pipe(
				map((record: any) => {
					return new UploadGroup(record.signedUrl, record.id, file, parentId);
				}),
				catchError(e => of(new GetLinkFail()))
			);
		})
	);

  @Effect()
	uploadGroup$: Observable<Action> = this.actions$.pipe(
		ofType<UploadGroup>(SustainabilityActionTypes.UploadGroup),
		map(action => [action.link, action.fileId, action.file, action.parentId]),
		switchMap(([link, fileId, file, parentId]) => {
			return this.sustainabilityApi.uploadGroup(link, file).pipe(
				map(record => {
					return new UploadGroupSuccess(fileId, parentId);
				}),
				catchError(e => of(new UploadGroupFail()))
			);
		})
	);

  @Effect()
	uploadGroupSuccess$ = this.actions$.pipe(
		ofType<UploadGroupSuccess>(SustainabilityActionTypes.UploadGroupSuccess),
		map(action => ([action.fileId, action.parentId])),
		switchMap(([fileId, parentId]) => {

			return this.sustainabilityApi
				.processUploadGroup(fileId, parentId)
				.pipe(
					map(record => {
						return new UploadingGroupPending({ status: 'pending', message: 'Cargando...'});
					}),
					catchError(e => of(new ExportGroupDownloadFail(e)))
				);
		})
	);

  @Effect()
  uploadingGroupSuccess$ = this.actions$.pipe(
    ofType<UploadingGroupSuccess>(
      SustainabilityActionTypes.UploadingGroupSuccess
    ),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Sostenibilidad actualizadas!' });
      return new LoadProvidersByParentId(action.data['parentId']);
    })
  );

  @Effect({ dispatch: false})
  exportConsolidate$ = this.actions$.pipe(
    ofType<ExportConsolidate>(SustainabilityActionTypes.ExportConsolidate),
    map(action => [action.parentId]),
    tap(([parentId]) => {
      this.sustainabilityApi.exportConsolidate(parentId);
    })
  );

  @Effect({ dispatch: false })
  exportGroup$ = this.actions$.pipe(
    ofType<ExportGroup>(SustainabilityActionTypes.ExportGroup),
    map(action => [action.parentId]),
    tap(([parentId]) => {
      this.sustainabilityApi.exportProviders(parentId);
    })
  );

  @Effect()
  exportProformasGroupSuccess$ = this.actions$.pipe(
    ofType<ExportGroupSuccess>(SustainabilityActionTypes.ExportGroupSuccess),
    map(action => {
      return new ExportGroupDownload(action.data.downloadUrl, action.data.name, action.data.total);
    })
  );

  @Effect({ dispatch: false })
  exportProformasGroupDownload$ = this.actions$.pipe(
    ofType<ExportGroupDownload>(SustainabilityActionTypes.ExportGroupDownload),
    map(action => [action.downloadUrl, action.name, action.total]),
    tap(([downloadUrl, name, total]) => {
      if (total > 0) {
        this.sustainabilityApi.downloadGroup(downloadUrl, name);
      }
    })
  );

  @Effect({ dispatch: false })
  exportProformasGroupDownloadSuccess$ = this.actions$.pipe(
    ofType<ExportGroupDownloadSuccess>(
      SustainabilityActionTypes.ExportGroupDownloadSuccess
    ),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Sostenibilidad descargadas!' });
    })
  );
  

  @Effect()
  acceptedChanges$ = this.actions$.pipe(
    ofType<AcceptedChanges>(SustainabilityActionTypes.AcceptedChanges),
    map(action => [action.data]),
    switchMap(data =>
      this.sustainabilityApi.acceptedChanges(data).pipe(
        map(res => {return new AcceptedChangesSuccess(data)}),
        catchError(error => of(new AcceptedChangesFail(error)))
      )
    )
  );

  @Effect({ dispatch: false })
  uploadProcessFileSuccess$ = this.actions$.pipe(
    ofType<UploadProcessFileSuccess>(SustainabilityActionTypes.UploadProcessFileSuccess),
    map((action) => {
      if(action.data && action.data.message){
        this.fs.emit({ type: 'success', message: action.data.message });
      }
    }),
  );

  @Effect({ dispatch: false })
  acceptedChangesSuccess$ = this.actions$.pipe(
    ofType<AcceptedChangesSuccess>(SustainabilityActionTypes.AcceptedChangesSuccess),
    map((action) => {
      this.fs.emit({ type: 'success', message: 'Actualizado correctamente' });
      this.router.navigate([`/sustainability/`,action.data[0].ParentId])
    }),
  );


  constructor(
    private actions$: Actions,
    private sustainabilityApi: SustainabilityService,
    private fs: FlashService,
    private router: Router
  ) {}
}
